import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Card from '../components/Card';
import Input from '../components/Input';
import Button from '../components/Button';
import { useSession } from '../context/SessionContext';
import api from '../utils/api';

const CreateSession = () => {
  const [formData, setFormData] = useState({
    name: ''
  });
  const [errors, setErrors] = useState({});
  const [createdSession, setCreatedSession] = useState(null);
  const { createSession, loading, error } = useSession();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Session name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const newSession = await createSession({
        name: formData.name
      });
      
      // Ensure we have the required session data
      if (!newSession || !newSession.id || !newSession.name) {
        console.error('Invalid session data received:', newSession);
        throw new Error('Invalid session data received');
      }

      // Store the created session with password for display
      setCreatedSession(newSession);

      // Assign default template for the session
      try {
        // Get all templates
        const templatesResponse = await api.get('/api/templates');
        
        // Find the Modern template
        const modernTemplate = templatesResponse.data.find(template => template.name === 'Modern');
        
        if (modernTemplate) {
          // Assign the Modern template to the session
          await api.post(`/api/sessions/${newSession.id}/template`, {
            templateId: modernTemplate.id
          });

          // Set default position settings
          await api.post(`/api/positions/session/${newSession.id}`, {
            positionX: 3,
            positionY: 5
          });
        } else {
          console.error('Modern template not found');
        }
      } catch (err) {
        console.error('Failed to assign default template or position:', err);
        // Continue even if template assignment fails
      }

      // Load existing sessions first to ensure we have the latest
      let currentSessions = [];
      try {
        const saved = localStorage.getItem('recentSessions');
        if (saved) {
          currentSessions = JSON.parse(saved);
        }
      } catch (err) {
        console.error('Failed to load existing sessions:', err);
      }
      
      // Add to recent sessions
      const updatedSessions = [
        {
          id: newSession.id,
          name: newSession.name,
          lastAccessed: new Date().toISOString()
        },
        ...currentSessions.filter(s => s.id !== newSession.id).slice(0, 4) // Keep last 5 sessions
      ];
      
      // Save to localStorage
      localStorage.setItem('recentSessions', JSON.stringify(updatedSessions));
      
      // Track session creation
      if (window.umami) {
        window.umami.track('Session Created');
      }
    } catch (err) {
      console.error('Failed to create session:', err);
      // Error is handled by the context and displayed below
    }
  };

  return (
    <Layout title="Create Session" backLink="/">
      <div className="max-w-md mx-auto">
        <Card>
          {createdSession ? (
            <div className="text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">🎉 Session Created Successfully!</h2>
                <p className="text-gray-600 mb-6">Your session is ready to use. Below you'll find the management password and URL. <strong>Save this information now</strong> - you'll need the password to access the session manager when you're ready.</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="text-sm text-gray-600 mb-2">Session Name:</div>
                <div className="font-mono text-lg font-semibold text-gray-900 mb-4">{createdSession.name}</div>
                
                <div className="text-sm text-gray-600 mb-2">Management Password:</div>
                <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-3 mb-4 flex items-center justify-between">
                  <div className="font-mono text-2xl font-bold text-gray-900 tracking-wider">
                    {createdSession.password}
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(createdSession.password);
                      // You could add a toast notification here
                    }}
                    className="ml-4 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Copy password"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
                
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <div className="text-sm text-gray-600 mb-2">Management URL:</div>
                <div className="bg-white border border-gray-300 rounded-lg p-3 mb-4 flex items-center justify-between">
                  <div className="font-mono text-sm text-gray-900 break-all">
                    {window.location.origin}/session/{createdSession.id}/manage
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/session/${createdSession.id}/manage`);
                    }}
                    className="ml-4 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Copy URL"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="mb-4">
                <Button
                  onClick={() => {
                    const info = `Session: ${createdSession.name}
Password: ${createdSession.password}
Management URL: ${window.location.origin}/session/${createdSession.id}/manage`;
                    navigator.clipboard.writeText(info);
                    // You could add a toast notification here
                  }}
                  variant="outline"
                  fullWidth
                  className="mb-3"
                >
                  📋 Copy All Information
                </Button>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate(`/session/${createdSession.id}/manage`)}
                  fullWidth
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Go to Session Manager
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
                  {error}
                </div>
              )}
              
              <Input
                label="Session Name"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter a name for your session"
                error={errors.name}
                required
              />
              
              <div className="mt-6">
                <Button
                  type="submit"
                  fullWidth
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Session'}
                </Button>
              </div>
            </form>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default CreateSession; 